'use client';

import * as React from 'react';
import styles from './QualityScore.module.css';
import clsx from 'clsx';

export interface QualityScoreProps {
  /** The score value (0-100) */
  value: number;
  /** Title displayed above the score */
  title?: string;
  /** Size of the circular progress indicator */
  size?: number;
  /** Thickness of the progress ring */
  thickness?: number;
  /** Optional action link text */
  actionLabel?: string;
  /** Callback when action link is clicked */
  onAction?: () => void;
  /** Additional class name */
  className?: string;
}

type PerformanceLevel = 'poor' | 'okay' | 'good' | 'great';

interface PerformanceInfo {
  text: string;
  level: PerformanceLevel;
}

function getPerformanceInfo(percentage: number): PerformanceInfo {
  if (percentage >= 0 && percentage <= 25) {
    return { text: 'Poor!', level: 'poor' };
  } else if (percentage > 25 && percentage <= 50) {
    return { text: 'Okay!', level: 'okay' };
  } else if (percentage > 50 && percentage <= 75) {
    return { text: 'Good!', level: 'good' };
  } else if (percentage > 75 && percentage <= 100) {
    return { text: 'Great!', level: 'great' };
  }
  return { text: 'Invalid!', level: 'poor' };
}

export const QualityScore: React.FC<QualityScoreProps> = ({
  value,
  title = 'Float Score',
  size = 100,
  thickness = 12,
  actionLabel,
  onAction,
  className,
}) => {
  const normalizedValue = Math.max(0, Math.min(100, value));
  const performance = getPerformanceInfo(normalizedValue);

  // Animate from ~10% to final value on mount
  const [animatedValue, setAnimatedValue] = React.useState(10);

  React.useEffect(() => {
    // Small delay before starting animation
    const timer = setTimeout(() => {
      setAnimatedValue(normalizedValue);
    }, 100);

    return () => clearTimeout(timer);
  }, [normalizedValue]);

  // SVG circle calculations
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedValue / 100) * circumference;
  const center = size / 2;

  // Unique ID for gradient (in case multiple instances)
  const gradientId = React.useId();

  return (
    <div className={clsx(styles.container, className)}>
      <span className={styles.header}>{title}</span>

      <div className={styles.scoreContainer}>
        <svg
          width={size}
          height={size}
          className={styles.progressForeground}
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e50b71" />
              <stop offset="100%" stopColor="#13319a" />
            </linearGradient>
          </defs>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={thickness}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: 'stroke-dashoffset 1s ease-out',
            }}
          />
        </svg>
      </div>

      <div className={styles.scoreInfo}>
        <p className={styles.scoreValue}>{Math.round(normalizedValue)}%</p>
        <p className={clsx(
          styles.scoreLabel,
          styles[`score${performance.level.charAt(0).toUpperCase() + performance.level.slice(1)}` as keyof typeof styles]
        )}>
          {performance.text}
        </p>
      </div>

      {actionLabel && onAction && (
        <a
          href="#"
          className={styles.actionLink}
          onClick={(e) => {
            e.preventDefault();
            onAction();
          }}
        >
          {actionLabel}
        </a>
      )}
    </div>
  );
};
